import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ArrowRight, FileText, Calculator, Star, Clock, Beaker, Atom } from "lucide-react";
import { NavBar, Footer } from "../components/Layout";

const SN4_COVER = "https://customer-assets.emergentagent.com/job_study-docs-1c/artifacts/xda5n1zx_IMG_1772.jpeg";
const CST4_COVER = "https://customer-assets.emergentagent.com/job_study-docs-1c/artifacts/674sas0x_IMG_1774.jpeg";
const MECA_COVER = "https://static.prod-images.emergentagent.com/jobs/fb03f6d6-6305-409a-adb6-3fa2acdfc7db/images/a50239aac0225a7d1fb4eb87d874ba720bc5f83025f9e51719b007380bc202bc.jpeg";

const ProductCard = ({ title, subtitle, price, cover, link, isNew, alt }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    className="card-dark hover:border-amber-500/30 transition-all duration-300 overflow-hidden group glow-blue relative">
    {isNew && (
      <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-orange-500 to-amber-500 text-slate-900 font-bold text-xs px-3 py-1 rounded-full shadow-lg">
        NOUVEAU
      </div>
    )}
    <div className="flex justify-center mb-6">
      <img src={cover} alt={alt || title} className="h-72 object-contain rounded-xl group-hover:scale-105 transition-transform duration-300 shadow-xl" loading="lazy" />
    </div>
    <h3 className="font-display font-bold text-xl text-white mb-1">{title}</h3>
    <p className="text-blue-300/70 text-sm mb-4">{subtitle}</p>
    <div className="flex items-center justify-between">
      <span className="text-2xl font-bold text-amber-400">{price}$ <span className="text-sm text-blue-300/50 font-normal">CAD</span></span>
      <Link to={link} data-testid={`view-${link.replace('/', '')}-button`} className="btn-gold text-sm flex items-center gap-2">
        Voir le guide <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </motion.div>
);

const HomePage = () => (
  <div className="min-h-screen bg-navy-900">
    <NavBar active="home" />

    {/* Hero - compact */}
    <section className="relative py-14 sm:py-20 overflow-hidden gradient-hero grid-overlay">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[80px] translate-y-1/3 -translate-x-1/4"></div>
      <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="badge-gold mb-6 inline-flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Conforme au programme du Québec
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl text-white leading-tight mb-6">
            Livres de mathématiques<br />SN4 et CST4<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">Tutorat et aide aux devoirs au Québec</span>
          </h1>
          <p className="text-lg text-blue-200/70 max-w-2xl mx-auto mb-6">
            Livres de mathématiques complets pour le <strong className="text-white">secondaire 4 au Québec</strong>.
            Tutorat de mathématiques, aide aux devoirs et <strong className="text-white">préparation aux examens du ministère</strong>.
          </p>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 max-w-2xl mx-auto mb-8 backdrop-blur-sm">
            <p className="text-blue-100/80 text-sm">
              <strong className="text-amber-400">Notre approche pédagogique</strong><br/><br/>Nous privilégions des questions courtes, claires et simplifiées qui vont directement à l'essentiel. Notre objectif est d'amener l'élève à développer sa capacité à décomposer un problème complexe en une série de questions simples. Cette méthode favorise une meilleure compréhension, facilite la résolution des problèmes et renforce l'autonomie de l'élève.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/sn4" className="btn-gold flex items-center gap-2 text-lg px-8 py-4" aria-label="Voir le guide de révision mathématiques SN4">Maths SN4 - 26,99$ <ArrowRight className="w-5 h-5" /></Link>
            <Link to="/cst4" className="btn-outline-light flex items-center gap-2 text-lg px-8 py-4" aria-label="Voir le guide de révision mathématiques CST4">Maths CST4 - 24,99$ <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Value Proposition Section - RIGHT AFTER HERO */}
    <section className="relative py-16 sm:py-20 bg-navy-800 overflow-hidden">
      <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-amber-500/5 rounded-full blur-[80px] -translate-y-1/2 -translate-x-1/4"></div>
      <div className="max-w-4xl mx-auto px-4 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-6">
              Réflexion pour les parents
            </h2>
            <div className="w-20 h-1.5 bg-gradient-to-r from-amber-400 to-yellow-300 mx-auto rounded-full mb-8"></div>
          </div>

          {/* Hook - cost saving FIRST for maximum impact */}
          <div className="bg-gradient-to-r from-amber-500/15 to-yellow-500/10 border border-amber-500/25 rounded-2xl p-8 mb-10">
            <p className="text-white text-xl sm:text-2xl font-display font-bold text-center leading-snug">
              Et si votre enfant pouvait essayer de comprendre{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">par lui-même</span>{" "}
              avant de recourir au tutorat?
            </p>
          </div>

          {/* Cost saving argument - HIGH IMPACT */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-10 backdrop-blur-sm text-center">
            <p className="text-amber-400 font-bold text-lg mb-2">Pourquoi payer 40$ à 60$ l'heure de tutorat</p>
            <p className="text-blue-100/80">
              quand une difficulté peut parfois être résolue avec une <strong className="text-white">bonne explication</strong> et de la <strong className="text-white">pratique</strong>?
              Un bon livre de référence coûte moins cher qu'une seule séance de tutorat et reste disponible <strong className="text-amber-400">en tout temps</strong>.
            </p>
          </div>

          {/* Intro */}
          <p className="text-blue-200/70 text-center text-lg mb-8 max-w-3xl mx-auto">
            Le tutorat peut être très utile, mais il n'est pas toujours nécessaire dès qu'une difficulté apparaît.
            Avec un bon livre, votre enfant peut :
          </p>

          {/* Benefits grid */}
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {[
              { text: "Développer davantage son autonomie", icon: "🚀" },
              { text: "Revoir une notion au moment où il en a besoin", icon: "🔄" },
              { text: "Relire une explication à son propre rythme", icon: "📖" },
              { text: "Faire plusieurs exercices pour vérifier sa compréhension", icon: "✏️" },
              { text: "Voir des exemples avant de demander de l'aide", icon: "💡" },
              { text: "Revenir facilement sur une notion avant un examen", icon: "🎯" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex items-start gap-3 bg-white/5 border border-white/5 rounded-xl p-4 hover:border-amber-500/20 transition-all"
              >
                <span className="text-lg flex-shrink-0 mt-0.5">{item.icon}</span>
                <span className="text-blue-100/80 text-sm">{item.text}</span>
              </motion.div>
            ))}
          </div>

          {/* Value proposition */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6 backdrop-blur-sm">
            <p className="text-blue-100/80 leading-relaxed text-center">
              Les livres <strong className="text-white">SN4</strong> et <strong className="text-white">CST4</strong> d'Excell-Tutorat ont été conçus comme des <strong className="text-amber-400">outils de travail accessibles en tout temps</strong>.
              L'objectif n'est pas de remplacer le tutorat, mais de donner à l'élève les outils pour <strong className="text-white">essayer d'abord par lui-même</strong>.
            </p>
          </div>

          {/* Closing line */}
          <div className="text-center mb-10">
            <p className="text-blue-200/50 text-sm mb-3">Et lorsque cela ne suffit pas, le tutorat reste là pour aller plus loin.</p>
            <p className="text-white font-display font-bold text-xl sm:text-2xl">
              Comprendre, pratiquer, progresser… <span className="text-amber-400">avant de demander de l'aide.</span>
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/sn4" className="btn-gold flex items-center gap-2 px-6 py-3" aria-label="Voir le cahier SN4">
              Cahier SN4 - 26,99$ <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/cst4" className="btn-outline-light flex items-center gap-2 px-6 py-3" aria-label="Voir le cahier CST4">
              Cahier CST4 - 24,99$ <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* NEW - Mécanique spotlight */}
    <section className="py-16 bg-navy-800 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-bold mb-4 border border-orange-500/20">
            NOUVEAU
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-white">Physique mécanique - Cégep 203-SN1-Re</h2>
        </motion.div>
        <div className="max-w-4xl mx-auto">
          <ProductCard
            title="Mécanique en action"
            subtitle="Physique mécanique - Cégep 203-SN1-Re - 200+ exercices résolus"
            price="29,99"
            cover={MECA_COVER}
            link="/mecanique"
            isNew={true}
            alt="Livre Mécanique en action - Exercices résolus physique 203-SN1-Re cégep Québec"
          />
        </div>
      </div>
    </section>

    {/* Products Math */}
    <section className="py-20 bg-navy-900">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-white text-center mb-12">Livres de mathématiques secondaire 4</h2>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <ProductCard title="Mathématiques SN4" subtitle="Sciences Naturelles - Secondaire 4" price="26,99" cover={SN4_COVER} link="/sn4" alt="Livre mathématiques SN4 secondaire 4 Québec" />
          <ProductCard title="Mathématiques CST4" subtitle="Culture, Société et Technique - Secondaire 4" price="24,99" cover={CST4_COVER} link="/cst4" alt="Livre mathématiques CST4 secondaire 4 Québec" />
        </div>
      </div>
    </section>

    {/* Stats */}
    <section className="py-16 bg-navy-900 border-y border-white/5">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {[
            { n: "120+", l: "Pages par guide", icon: FileText, c: "text-blue-400" },
            { n: "17", l: "Chapitres complets", icon: BookOpen, c: "text-emerald-400" },
            { n: "300+", l: "Exercices résolus", icon: Calculator, c: "text-amber-400" },
            { n: "2", l: "Guides disponibles", icon: Star, c: "text-purple-400" },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="text-center p-6 bg-white/5 rounded-2xl border border-white/5">
              <s.icon className={`w-8 h-8 ${s.c} mx-auto mb-3`} />
              <div className={`text-3xl sm:text-4xl font-bold ${s.c} mb-1`}>{s.n}</div>
              <div className="text-blue-200/60 text-sm">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* SEO Content Section */}
    <section className="py-20 bg-navy-900">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="font-display font-bold text-3xl text-white text-center mb-8">
            Votre partenaire de réussite en mathématiques au Québec
          </h2>
          <div className="prose prose-invert max-w-none text-blue-200/70 space-y-4 text-sm leading-relaxed">
            <p>
              <strong className="text-white">Excell-Tutorat</strong> est votre ressource de <strong className="text-white">tutorat en mathématiques</strong> et de <strong className="text-white">soutien scolaire en ligne au Québec</strong>. Nous offrons des cahiers de révision complets, des exercices corrigés et des aide-mémoire pour les élèves du <strong className="text-white">secondaire 1 à 5</strong>. Nos ressources pédagogiques sont conçues pour faciliter l'<strong className="text-white">aide aux devoirs</strong>, l'<strong className="text-white">accompagnement scolaire</strong> et la <strong className="text-white">préparation aux examens du ministère de l'Éducation du Québec</strong>.
            </p>
            <p>
              Que tu cherches un <strong className="text-white">cahier d'exercices en mathématiques</strong>, un <strong className="text-white">livre numérique</strong> en PDF, des <strong className="text-white">méthodes de résolution</strong> efficaces ou du <strong className="text-white">rattrapage scolaire en mathématiques</strong>, nos guides couvrent tout le programme. Chaque document inclut des résumés théoriques, des exercices progressifs et des solutions détaillées étape par étape pour une <strong className="text-white">étude efficace</strong>.
            </p>
            <p>
              Nos guides sont disponibles en <strong className="text-white">math secondaire 4 SN</strong> (Sciences Naturelles) et <strong className="text-white">CST</strong> (Culture, Société et Technique), avec bientôt des guides pour le <strong className="text-white">secondaire 1, 2, 3 et 5</strong>. C'est l'outil idéal pour les élèves, les parents et les enseignants qui cherchent des <strong className="text-white">cours particuliers en mathématiques</strong> et des <strong className="text-white">ressources pédagogiques</strong> de qualité au Québec.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-8 justify-center">
            <Link to="/sn4" className="text-amber-400 hover:text-amber-300 text-sm font-medium border border-amber-500/20 px-4 py-2 rounded-lg hover:bg-amber-500/10 transition-all">Révision math SN4</Link>
            <Link to="/cst4" className="text-amber-400 hover:text-amber-300 text-sm font-medium border border-amber-500/20 px-4 py-2 rounded-lg hover:bg-amber-500/10 transition-all">Révision math CST4</Link>
            <Link to="/gratuit" className="text-emerald-400 hover:text-emerald-300 text-sm font-medium border border-emerald-500/20 px-4 py-2 rounded-lg hover:bg-emerald-500/10 transition-all">Aide-mémoire gratuits</Link>
            <Link to="/contact" className="text-blue-400 hover:text-blue-300 text-sm font-medium border border-blue-500/20 px-4 py-2 rounded-lg hover:bg-blue-500/10 transition-all">Contactez-nous</Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Coming Soon */}
    <section className="py-20 bg-navy-800 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <div className="badge-gold mb-6 inline-flex items-center gap-2"><Clock className="w-4 h-4" /> Prochainement</div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-white mb-4">Bientôt : exercices de math secondaire 1 à 5</h2>
        <p className="text-blue-200/60 text-lg max-w-2xl mx-auto mb-12">Maths, Physique et Chimie pour secondaire 4 et 5 en SN, CST et TS.</p>
        <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
          {[
            { icon: Calculator, title: "Mathématiques", c: "amber" },
            { icon: Atom, title: "Physique", c: "blue" },
            { icon: Beaker, title: "Chimie", c: "purple" },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="card-dark text-center">
              <item.icon className={`w-8 h-8 text-${item.c}-400 mx-auto mb-3`} />
              <h3 className="font-semibold text-white mb-1">{item.title}</h3>
              <p className="text-blue-300/50 text-sm mb-3">Sec 4 et 5</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {["SN", "CST", "TS"].map(t => <span key={t} className="bg-white/5 text-blue-300/60 text-xs px-2 py-1 rounded">{t}</span>)}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default HomePage;
